import React, {ReactElement, useState} from "react"
import {useAccessToken} from "../../services/auth"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {usePaging} from "../../hooks/usePaging"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {findEvents, findManagedSolutionsEvents, Event} from "../../resources/webhook"
import moment from "moment"
import PagingNavBar from "../../components/PagingNavBar/PagingNavBar"
import {
	DataTableRow,
	DataTableCell,
	DataTableHead,
	DataTableBody,
	DataTableActionHeader,
	DataTable,
	DataTableCard,
	TablePending,
} from "../DataTable/DataTable"
import classNames from "classnames"
import DatePickerWithPresets, {DatePickerPresetKeys} from "../DatePicker/DatePicker"
import {useQueryState} from "use-location-state"
import Drawer from "../Drawer/Drawer"
import ReactJson from "react-json-view"

export enum EventsColumns {
	id = "Id",
	type = "Type",
	createdAt = "Created At",
}

type AllowedEventsColumns = EventsColumns.id | EventsColumns.type | EventsColumns.createdAt

function EventRow({
	event,
	toggleEventDrawer,
	includedColumns,
}: {
	event: Event
	toggleEventDrawer: (event: Event) => void
	includedColumns: Array<AllowedEventsColumns>
}) {
	const contentColumns: Partial<Record<AllowedEventsColumns, ReactElement>> = {}

	if (includedColumns.includes(EventsColumns.id)) {
		contentColumns["Id"] = (
			<DataTableCell className={classNames("data-table-id-cell")}>
				<span className="data-table-id"> {event.id} </span>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(EventsColumns.type)) {
		contentColumns["Type"] = <DataTableCell>{event.type}</DataTableCell>
	}

	if (includedColumns.includes(EventsColumns.createdAt)) {
		contentColumns["Created At"] = (
			<DataTableCell>{moment(event.attributes.createdAt).format("L hh:mm:ss.SSS A")}</DataTableCell>
		)
	}

	const content: Array<ReactElement | null> = []

	includedColumns.forEach((col, i) => {
		if (col in contentColumns && contentColumns[col]) {
			const column = {...contentColumns[col]} as ReactElement
			column.key = i.toString()
			content.push(column)
		}
	})

	return <DataTableRow onClick={() => toggleEventDrawer(event)}>{content}</DataTableRow>
}

export function Events({
	fullHeight = true,
	enableTitle = false,
	includedColumns,
	refreshToken,
	useRtlEndpoint = false,
}: {
	fullHeight?: boolean
	enableTitle?: boolean
	includedColumns: Array<AllowedEventsColumns>
	refreshToken?: number
	useRtlEndpoint?: boolean
}) {
	const prefix = "events-"
	const accessToken = useAccessToken()
	const [_] = useRefreshToken()
	const [since, setSince] = useQueryState(`${prefix}-filter[since]`, "")
	const [until, setUntil] = useQueryState(`${prefix}-filter[until]`, "")
	const [selectedEvent, setSelectedEvent] = useState<Event | null>(null)
	const [isEventDrawerOpen, setIsEventDrawerOpen] = useState(false)

	const toggleEventDrawer = (event?: Event) => {
		if (event) {
			setSelectedEvent(event)
		}
		setIsEventDrawerOpen((prevState) => !prevState)
	}

	const fetchEvents = useRtlEndpoint ? findManagedSolutionsEvents : findEvents

	const [result, hasPrev, hasNext, prev, next, hasResults] = usePaging(
		25,
		(offset, limit) => fetchEvents(accessToken, offset, limit, since, until),
		(x) => x.data.length,
		[refreshToken, _, since, until, useRtlEndpoint],
		prefix,
		true
	)

	const dateTime = (
		<DatePickerWithPresets
			calendarPosition={"right"}
			presets={[
				DatePickerPresetKeys.allTime,
				DatePickerPresetKeys.yesterday,
				DatePickerPresetKeys.lastMonth,
				DatePickerPresetKeys.last3Months,
				DatePickerPresetKeys.last6Months,
				DatePickerPresetKeys.last30Days,
				DatePickerPresetKeys.last7Days,
				DatePickerPresetKeys.custom,
			]}
			onDateChanged={(s, u) => {
				setSince(s)
				setUntil(u)
			}}
		/>
	)

	return (
		<DataTableCard className={"events-card"}>
			<DataTableActionHeader
				dateTimePicker={dateTime}
				enableSticky={fullHeight}
				title={enableTitle ? "Events" : null}
			/>
			<AsyncResultComponent
				asyncResult={result}
				pendingComponent={<TablePending numberOfRows={includedColumns.length} />}
			>
				{({value: events}) => {
					const rows = events.data.map((e) => (
						<EventRow includedColumns={includedColumns} key={e.id} event={e} toggleEventDrawer={toggleEventDrawer} />
					))

					return (
						<div className={"events-table"}>
							<DataTable>
								<DataTableHead>
									<DataTableRow>
										{Object.entries(includedColumns).map((column) => {
											return <DataTableCell key={column[0]}>{column[1]}</DataTableCell>
										})}
									</DataTableRow>
								</DataTableHead>
								<DataTableBody>{rows}</DataTableBody>
							</DataTable>
							<PagingNavBar hasResults={hasResults} hasPrev={hasPrev} hasNext={hasNext} prev={prev} next={next} />
						</div>
					)
				}}
			</AsyncResultComponent>
			{selectedEvent && (
				<Drawer
					open={isEventDrawerOpen}
					onClose={() => toggleEventDrawer()}
					direction="right"
					className="event-side-drawer"
					title={`Event Details id ${selectedEvent.id}`}
				>
					{isEventDrawerOpen && (
						<div className="p-4" style={{width: 800}}>
							<ReactJson name={"data"} src={selectedEvent} />
						</div>
					)}
				</Drawer>
			)}
		</DataTableCard>
	)
}
